import {
  ActiveVariantType,
  SavedFilter,
  GeneSet,
  Test,
  SelectedSavedFilters,
} from '../types';
import { buildTableData } from './buildTableData';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { pwtSharedMessages } from '@lifeomic/phc-web-toolkit/src/common/intlLabels';
import { reduceFilters } from '../FilterResultsManager/utils';
import { messages } from './intlLabels';
import { TableModule } from '@lifeomic/chroma-react/components/TableModule';
import { useActiveAccountId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveAccountId';
import { useActiveProjectId } from '@lifeomic/phc-web-toolkit/src/hooks/useActiveProjectId';
import { useCustomCompareEffect } from 'react-use';
import { defineMessages, useIntl } from 'react-intl';
import { useOrderByTableData } from '../useOrderByTableData';
import { VariantDetails } from '../VariantDetails/VariantDetails';
import * as React from 'react';
import GenomicsService from '@lifeomic/phc-web-toolkit/src/services/GenomicsService';
import useService from '@lifeomic/phc-web-toolkit/src/hooks/useService';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { AlertTriangle } from '@lifeomic/chromicons';
import { SelectedSequenceTypeProvider } from '../SequenceTypeContext';
import {
  getAAChangeTextOnly,
  getGeneTextOnly as getShortGeneTextOnly,
  getNNChange,
  getVariantClassTextOnly,
} from '../FilterResultsManager/ShortVariantsContent/buildTableData';
import {
  getEffect as getCopyNumberEffect,
  getGeneTextOnly as getCopyNumberGeneTextOnly,
} from '../FilterResultsManager/CopyNumberVariantsContent/buildTableData';
import {
  getEffect as getStructuralEffect,
  getGeneTextOnly as getStructuralTextOnly,
} from '../FilterResultsManager/StructuralVariantsContent/buildTableData';
import {
  getExpression,
  getGeneTextOnly as getExpressionGeneTextOnly,
} from '../FilterResultsManager/ExpressionsContent/buildTableData';
import { isEqual } from 'lodash';

const genomicsService = new GenomicsService();

const modalMessages = defineMessages({
  shortVariantTitle: {
    id: 'omicsExplorer.summaryTestTable.modalTitle.shortVariantTitle',
    defaultMessage: '{gene} {aa} {nn} {class}',
  },
  geneEffectTitle: {
    id: 'omicsExplorer.summaryTestTable.modalTitle.geneEffectTitle',
    defaultMessage: '{gene} {effect}',
  },
  geneExpressionTitle: {
    id: 'omicsExplorer.summaryTestTable.modalTitle.geneExpressionTitle',
    defaultMessage: '{gene} {expression}',
  },
});

const getSearchKeyAndValueFromDefaultFilter = (
  defaultFilter: SavedFilter,
  addEnsembl = false,
) => {
  if (!defaultFilter?.params?.filters) {
    return null;
  }

  // ensembl isn't a true saved filter so we're adding it here based up the callers logic
  const filters = reduceFilters(defaultFilter?.params?.filters);
  if (addEnsembl) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable noImplicitAny.
    filters['ensembl'] = true;
  }

  return filters;
};

// @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
const convertType = (type): ActiveVariantType => {
  if (!type) {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    return null;
  }

  switch (type) {
    case 'shortVariant':
      return 'short';
    case 'structuralVariant':
      return 'structural';
    case 'copyNumberVariant':
      return 'copyNumber';
    case 'expression':
      return 'expression';
  }
};

const useStyles = makeStyles(theme => ({
  root: {},
  innerIconButton: {
    '& > svg': {
      width: theme.pxToRem(14),
      height: theme.pxToRem(14),
    },
  },
  warningIcon: {
    marginRight: theme.spacing(2),
  },
  warningBox: {
    padding: theme.spacing(2),
  },
}));

interface SummaryTestTableProps {
  test: Test;
  geneSets?: Array<GeneSet>;
  selectedSavedFilters: SelectedSavedFilters;
}

export const SummaryTestTable: React.FC<SummaryTestTableProps> = ({
  test,
  geneSets,
  selectedSavedFilters,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();

  const activeProjectId = useActiveProjectId();
  const activeAccountId = useActiveAccountId();
  const isFountainLifeAccount = !!activeAccountId?.startsWith('fountainlife');
  const aminoAcidField = isFountainLifeAccount
    ? 'hgvsAminoAcidChange'
    : 'aminoAcidChange';

  const [getSummary, getSummaryState] = useService(genomicsService.getSummary);

  const [summaryForTest, setSummaryForTest] = React.useState(null);
  const [dialogState, setDialogState] = React.useState<{
    isOpen: boolean;
    data: any;
    title: string;
  }>({
    isOpen: false,
    data: null,
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    title: null,
  });

  const { handleSortClicked, sortedData } = useOrderByTableData(
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    summaryForTest?.variants,
  );

  useCustomCompareEffect(
    () => {
      if (!selectedSavedFilters || !Object.keys(selectedSavedFilters).length) {
        return;
      }

      // Reset summary if new filters are selected
      if (summaryForTest) {
        setSummaryForTest(null);
      }

      const acmg59GeneSet = geneSets?.find(
        geneSet => geneSet.name === 'ACMG 59',
      );

      // This can be really confusing, but the logic here is:
      // Use any default filter for the somatic/germline +
      // short/expression/structural/copyNumber combination.  If we don't have those,
      // we use the specified default options.
      // The Germline case is unique, as we rely on having the ACMG 59 gene set,
      // but if we don't have that we have fallback to the default options as well.
      getSummary({
        projectId: activeProjectId,
        testId: test.id,
        params: {
          somatic: {
            expressionFilter: getSearchKeyAndValueFromDefaultFilter(
              selectedSavedFilters?.somaticExpression,
            ) || {
              drugAssociations: true,
            },
            copyNumberFilter: getSearchKeyAndValueFromDefaultFilter(
              selectedSavedFilters.somaticCopyNumber,
            ) || {
              drugAssociations: true,
            },
            structuralFilter: getSearchKeyAndValueFromDefaultFilter(
              selectedSavedFilters.somaticStructural,
            ) || {
              drugAssociations: true,
            },
            shortFilter: getSearchKeyAndValueFromDefaultFilter(
              selectedSavedFilters.somaticShort,
              true,
            ) || {
              drugAssociations: true,
              ensembl: true,
              group: [
                'frameshift',
                'splice_site',
                'stop_lost',
                'start_lost',
                'nonsense',
                'inframe',
                'missense',
              ],
            },
          },
          germline: {
            expressionFilter:
              getSearchKeyAndValueFromDefaultFilter(
                selectedSavedFilters.germlineExpression,
              ) || {},
            copyNumberFilter:
              getSearchKeyAndValueFromDefaultFilter(
                selectedSavedFilters.germlineCopyNumber,
              ) || {},
            structuralFilter:
              getSearchKeyAndValueFromDefaultFilter(
                selectedSavedFilters.germlineStructural,
              ) || {},
            shortFilter: getSearchKeyAndValueFromDefaultFilter(
              selectedSavedFilters.germlineShort,
            ) || {
              gene: acmg59GeneSet
                ? // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
                  acmg59GeneSet.genes.map(g => g.gene)
                : undefined,
              clinvarSignificance: [
                'Pathogenic',
                'Likely pathogenic',
                'Pathogenic/Likely pathogenic',
              ],
            },
          },
        },
      });
    },
    [selectedSavedFilters],
    ([prevSelectedSavedFilters], [selectedSavedFilters]) =>
      isEqual(prevSelectedSavedFilters, selectedSavedFilters),
  );

  React.useEffect(() => {
    if (!getSummaryState?.response?.data) {
      return;
    }

    setSummaryForTest({
      ...test,
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      genomeBuild: test?.referenceSetId,
      variants: getSummaryState?.response?.data?.items,
      truncated: getSummaryState?.response?.data?.truncated,
    });
  }, [getSummaryState, test]);

  const handleRowClick = (row: any) => {
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    let title: string = null;

    const variantType = convertType(row.variantType);
    switch (variantType) {
      case 'short':
        title = formatMessage(modalMessages.shortVariantTitle, {
          gene: getShortGeneTextOnly(row),
          aa: getAAChangeTextOnly(row, aminoAcidField),
          nn: getNNChange(row),
          class: getVariantClassTextOnly(row),
        });
        break;
      case 'copyNumber':
        title = formatMessage(modalMessages.geneEffectTitle, {
          gene: getCopyNumberGeneTextOnly(row),
          effect: getCopyNumberEffect(row),
        });
        break;
      case 'expression':
        title = formatMessage(modalMessages.geneExpressionTitle, {
          gene: getExpressionGeneTextOnly(row),
          expression: getExpression(row),
        });
        break;
      case 'structural':
        title = formatMessage(modalMessages.geneEffectTitle, {
          gene: getStructuralTextOnly(row),
          effect: getStructuralEffect(row),
        });
        break;
      default:
        break;
    }
    setDialogState({ isOpen: true, data: row, title });
  };

  const handleDialogClose = () =>
    // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
    setDialogState({ isOpen: false, data: null, title: null });

  return (
    <>
      <Modal
        isFullScreen={true}
        title={dialogState.title}
        isOpen={dialogState.isOpen}
        onDismiss={handleDialogClose}
      >
        {dialogState.isOpen && (
          <SelectedSequenceTypeProvider>
            <VariantDetails
              activeSequenceFromProps={summaryForTest}
              data={dialogState.data}
              variantType={convertType(dialogState.data.variantType)}
              useSequenceTypeFromData
            />
          </SelectedSequenceTypeProvider>
        )}
      </Modal>
      {/* @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks. */}
      {summaryForTest?.truncated && (
        <Box fullWidth className={classes.warningBox}>
          <AlertTriangle
            className={classes.warningIcon}
            role="img"
            height={30}
            aria-hidden
          />
          <Box direction="column" fullWidth>
            <Text>{formatMessage(messages.truncationWarning)}</Text>
          </Box>
        </Box>
      )}
      <TableModule
        isLoading={getSummaryState.isFetching}
        rowClickLabel={formatMessage(pwtSharedMessages.viewDetails)}
        onRowClick={handleRowClick}
        config={buildTableData({
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          activeSequence: summaryForTest,
          account: activeAccountId,
          formatMessage,
          onSort: handleSortClicked,
          classes,
          // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
          summary: summaryForTest,
        })}
        data={sortedData}
        maxCellWidth={2}
      />
    </>
  );
};
